"use client";
import { DrupalNode } from "next-drupal";
import Link from "next/link";
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { MdChevronRight } from "react-icons/md";
import Image from "next/image";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { useState } from "react";
import { CaptchaProvider } from "@/lib/CaptchaService/Provider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/Dialog";
import geos from "../../../public/resources/mappings/countries.geo.json";
import FlipFlopCard from "@/components/sections/FlipFlopCard";
import Banner from "@/components/sections/Banner";
import { config } from "@/lib/config";
import { formatSubmissions } from "@/utils";
import { ScrollArea } from "@/components/ui/Dialog/scroll-area";
import ElevateJoiningForm from "@/components/forms/ElevateJoiningForm";
import PledgeSuccessModal from "@/components/sections/PledgeSuccessModal";
import FindOutSection from "@/components/sections/FindOutSection";

interface ElevateScreenProps {
  data: DrupalNode;
  joinFormSubmissions: DrupalNode[];
  opportunityCards: DrupalNode[];
}

const ElevateScreen: React.FC<ElevateScreenProps> = ({
  data,
  joinFormSubmissions,
  opportunityCards,
}) => {
  const [isDesktop, setIsDesktop] = useState<boolean>(false);
  const [isTablet, setIsTablet] = useState<boolean>(false);
  const [isOpenJoinForm, setIsOpenJoinForm] = useState<any>(false);
  const {
    title = "",
    field_elevate_subtitle = "",
    field_elevate_energy_title = "",
    field_energy_related_title = "",
    field_elevate_benefits_title = "",
    field_elevate_benefits_subtitle = "",
    field_energy_related_cards = [],
    field_elevate_benefits_cards = [],
    field_elevate_energy_content = {},
    field_elevate_efficiency_banner = {},
    field_elevate_training_section = {},
    field_elevate_bottom_footer = {},
    field_energy_efficiency_101 = [],
    field_energy_efficient_title = "",
    field_energy_efficient_content = {},
    field_energy_objective_images_ = [],
    field_energy_objective_content = {},
    field_enery_objective_title = "",
    field_energy_activity_cards = [],
    field_energy_activity_title = "",
    field_power_energy_title = "",
    field_power_energy_subtitle = "",
    field_power_energy_content = {},
    field_global_taskforce_title = "",
    field_global_taskforce_content = {},
    field_ready_to_join_content = {},
    field_ready_to_move_cards = [],
    field_ready_to_join_form = [],
    field_elevate_campaign_materials = [],
    field_elevate_campaign_resources = [],
    field_energy_efficient_image = "",
    field_ready_to_move_section = {},
    field_ready_to_move_main_image = "",
    field_opportunity_section_title = "",
    field_opportunity_section_subtit = "",
    field_power_objective_content = {},
    field_power_activity_content = {},
    field_power_energy_image = {},
    field_power_objective_image = {},
    field_power_activity_image = {},
    field_map_title = "",
    field_campaign_materials_title = "",
    field_campaign_resources_title = "",
    field_campaign_life_banner = [],
  } = data;

  const [hoveredCountryData, setHoveredCountryData] = useState<{
    name: string;
    records: number;
  }>({ name: "", records: 0 });
  const [openSuccessModal, setOpenSuccessModal] = useState<boolean>(false);
  const [showTooltip, setShowTooltip] = useState(true);
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;

  const handleMouseEnterCountry = (geo: any) => {
    let hoveredCountryName = geo.properties.name;
    const recordsInHoveredCountry = joinFormSubmissions.filter(
      (submissionData: DrupalNode) =>
        submissionData.data.country === hoveredCountryName,
    );

    const totalSubmissionsInHoveredCountry = recordsInHoveredCountry.length;
    if (totalSubmissionsInHoveredCountry > 0) {
      hoveredCountryName = recordsInHoveredCountry[0].data.country;
    }

    setHoveredCountryData({
      name: hoveredCountryName,
      records: totalSubmissionsInHoveredCountry,
    });
    setShowTooltip(true);
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowTooltip(false);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      if (window?.innerWidth > 1200) {
        setIsDesktop(true);
      }
      if (window?.innerWidth < 1024) {
        setIsTablet(true);
      }
    }
  }, []);

  return (
    <CaptchaProvider>
      <div className="pt-20 bg-white">
        <Banner
          title={title}
          description={field_elevate_subtitle}
          isRounded
          backgroundImg="/static/images/elevate-banner.webp"
        />
        {/* Energy Efficiency 101 */}
        <div
          id="energy-efficiency-101"
          className="pt-2 !bg-mapGray mobileMax:py-6 relative overflow-hidden"
        ></div>

        <div className="mini-container flex !bg-white items-center justify-between laptopMax:flex-col relative z-[2] pb-20">
          <div className="w-full mt-[72px]">
            <motion.h3
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0,
              }}
              className="remove-animation-fluctuation desktop:text-[32px] mb-[15px] leading-normal text-center bg-[linear-gradient(to_top,#8EBCE5,#11375C)] font-semibold text-clip text-[48px] mobileMax:text-[28px] mobileMax:px-0 mobileMax:mb-5"
            >
              {field_elevate_energy_title}
            </motion.h3>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0,
              }}
              className="remove-animation-fluctuation mt-5 mb-6 mobileMax:mb-8 text-center text-[#003350] text-[20px] !font-normal leading-normal --font-poppins mobileMax:text-small"
              dangerouslySetInnerHTML={{
                __html: field_elevate_energy_content.processed,
              }}
            />
            <div className="flex flex-wrap justify-start m-auto box-border">
              {/* this card reference */}
              {field_energy_efficiency_101?.map(
                (eneryEfficiencyCard: DrupalNode, index: number) => {
                  return (
                    <motion.div
                      key={index}
                      className="remove-animation-fluctuation px-[15px] mb-[25px] w-[33%] remove-animation-fluctuation mobileMax:w-full mt-2 mobileMax:px-0 lieTablets:w-[50%] mobileMax:mb-5 mobileMax:mt-0 "
                      initial={{ opacity: 0, y: 40 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0,
                      }}
                    >
                      <div className="transition rounded-xl bg-[linear-gradient(to_top,#003350,#226B8A)] p-5 h-full flex items-center flex-col box-border w-full card-shadow">
                        <div className="min-h-[80px] min-w-[80px]  rounded-full overflow-hidden flex items-center justify-center mb-3 mobileMax:min-h-[55px] mobileMax:min-w-[55px]">
                          <div className="flex items-center justify-center text-center text-[35px] rounded-full border-[4px] border-white overflow-hidden min-h-[70px] min-w-[70px] mobileMax:min-h-[45px] mobileMax:min-w-[45px] bg-transparent font-semibold leading-10 --font-poppins mobileMax:text-medium mobileMax:leading-7">
                            <p className="text-white">{index + 1}</p>
                          </div>
                        </div>
                        <div className="h-full flex items-center justify-between flex-col w-full">
                          <h4 className="text-center text-[22px] mb-3 text-white leading-7 font-semibold mobileMax:text-medium mobileMax:leading-7">
                            {eneryEfficiencyCard?.title}
                          </h4>
                          <div
                            className="--font-poppins text-center text-small mb-5 text-[#fff] leading-6 elevate-card-ellipse mobileMax:text-xsmall mobileMax:leading-normal font-normal"
                            dangerouslySetInnerHTML={{
                              __html:
                                eneryEfficiencyCard?.field_content.processed,
                            }}
                          />
                          <Link
                            href={eneryEfficiencyCard?.field_link || "#"}
                            target="_blank"
                            className="--font-poppins text-small text-white leading-6 flex items-center justify-center cursor-pointer  mobileMax:text-xsmall"
                          >
                            {eneryEfficiencyCard?.field_button_text}
                            <MdChevronRight className="w-[18px] h-[18px] max-w-[18px] ml-0.5" />
                          </Link>
                        </div>
                      </div>
                    </motion.div>
                  );
                },
              )}
            </div>
          </div>
        </div>

        {/* Energy efficiency banner */}
        <div className="py-[100px] mobileMax:pt-12 pb-16 betweenMobileTab:py-10 overflow-hidden !bg-[#003350]">
          <div className="mini-container">
            <motion.h3
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0,
              }}
              className="remove-animation-fluctuation bg-clip-text text-transparent text-clip bg-[#48DBB2] font-semibold tracking-tight text-[35px] leading-normal mb-[50px] text-center mobileMax:text-[28px] mobileMax:mb-8"
            >
              {field_energy_related_title}
            </motion.h3>
            <div className="flex flex-wrap justify-center box-border !overflow-hidden">
              {field_energy_related_cards?.map(
                (relatedCard: DrupalNode, index: number) => {
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 50 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0,
                      }}
                      className="remove-animation-fluctuation px-[15px] mb-[30px] w-[25%] betweenMobileTab:w-[50%] mobileMax:w-full mobileMax:px-0 mobileMax:mb-5"
                    >
                      <div className="rounded-xl h-full flex items-start flex-col box-border w-full">
                        <div className="mb-5 mx-auto h-[80px]">
                          <Image
                            src={`${config.apiBase}${
                              relatedCard?.field_image_icon?.uri?.url || ""
                            }`}
                            alt="category img"
                            height={80}
                            width={80}
                            className="h-full w-full"
                          />
                        </div>
                        <div className="h-full w-full">
                          <h4 className="--font-poppins text-center font-semibold text-medium mb-4 bg-clip-text text-transparent text-clip bg-[linear-gradient(to_bottom,#48DBB2,#4FC0FF)] leading-8 capitalize">
                            {relatedCard?.title}
                          </h4>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: relatedCard?.field_content?.processed,
                            }}
                            className="--font-poppins text-center text-xsmall text-[#fff] leading-6"
                          />
                        </div>
                      </div>
                    </motion.div>
                  );
                },
              )}
            </div>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0,
              }}
              className="remove-animation-fluctuation rounded-xl h-full w-full px-5 py-[48px] mobileMax:py-10 flex items-center justify-center flex-col box-border laptop:min-w-[340px]"
            >
              <div className="flex items-center flex-col box-border desktop:w-[60%] w-[70%] mobileMax:w-full">
                <div className="mb-8 h-[60px]">
                  <Image
                    src={`${config.apiBase}${
                      field_elevate_efficiency_banner?.field_image_icon?.uri
                        ?.url || ""
                    }`}
                    alt="category img"
                    className="h-full w-full"
                    height={80}
                    width={80}
                  />
                </div>
                <div className="h-full w-full">
                  <h4 className=" text-center text-[40px] mb-8 text-white tracking-tight leading-8 capitalize mobileMax:text-[28px] mobileMax:mb-6 mobileMax:leading-6">
                    {field_elevate_efficiency_banner?.title}
                  </h4>
                  <div
                    dangerouslySetInnerHTML={{
                      __html:
                        field_elevate_efficiency_banner?.field_content
                          ?.processed,
                    }}
                    className="--font-poppins text-center text-small text-[#fff] mb-4 leading-[26px] mobileMax:text-xsmall mobilemax:leading-normal"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
        {/*multiple benefit section */}
        <div
          id="multiple-benefit"
          className="pt-20 pb-20 relative bg-white mobileMax:pt-10 mobileMax:pb-5"
        >
          <div className="mini-container">
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0,
              }}
              className="remove-animation-fluctuation bg-[linear-gradient(to_top,#8EBCE5,#11375C)] font-semibold text-clip text-[32px] mb-[20px] mt-0 leading-tight text-center mobileMax:text-[28px] mobileMax:mb-5 betweenMobileTab:text-[32px]"
            >
              {field_elevate_benefits_title}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0,
              }}
              className="remove-animation-fluctuation --font-poppins text-[16px] text-center text-[#003350] leading-7 mb-20 mobileMax:text-xsmall mobileMax:leading-normal mobileMax:mb-10"
            >
              {field_elevate_benefits_subtitle}
            </motion.p>
            <div className="flex items-center flex-wrap">
              {field_elevate_benefits_cards?.map(
                (benefitsCard: DrupalNode, index: number) => {
                  return (
                    <motion.div
                      key={index}
                      className="remove-animation-fluctuation px-[15px] mb-[30px] w-[25%] betweenMobileTab:w-[50%] mobileMax:w-full mobileMax:px-0 mobileMax:mb-5"
                      initial={{ opacity: 0, y: 40 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0,
                      }}
                    >
                      <FlipFlopCard data={benefitsCard} />
                    </motion.div>
                  );
                },
              )}
            </div>
          </div>
        </div>
        {/* opportunity cards */}
        <div className=" py-[60px] bg-mapGray mobileMax:py-10">
          <div className="mini-container">
            <motion.h3
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0,
              }}
              className="remove-animation-fluctuation capitalize px-5 mobileMax:px-0 desktop:text-[32px] desktop:leading-[85px] text-center bg-[linear-gradient(to_top,#8EBCE5,#11375C)] font-semibold text-clip text-[32px] leading-[60px] mobileMax:text-[28px] mobileMax:leading-normal mobileMax:mb-5"
            >
              {field_opportunity_section_title}
            </motion.h3>
            <motion.p
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0,
              }}
              className="remove-animation-fluctuation text-[#003350] mb-12 text-[16px] text-center mobileMax:text-small mobileMax:mb-8"
            >
              {field_opportunity_section_subtit}
            </motion.p>

            <div className="max-w-4xl mx-auto px-4 desktop:px-8">
              <div className="relative">
                <div className="absolute left-1/2 mobileMax:top-[4rem] mobileMax:bottom-[5.2rem] betweenMobileTab:top-[3.9rem] betweenMobileTab:bottom-[4.3rem] top-[7.5rem] bottom-[7.5rem] w-1 bg-[#828282] transform -translate-x-1/2" />

                <div className="space-y-10 desktop:space-y-16 mt-[90px]">
                  {opportunityCards.map((item: DrupalNode, index: number) => (
                    <div
                      key={item.id}
                      className={`flex items-center gap-4 desktop:gap-[40px] ${
                        index % 2 != 0 ? "flex-row" : "flex-row-reverse"
                      }`}
                    >
                      <div
                        className={`flex-1 ${
                          index % 2 != 0 ? "text-right " : "text-left"
                        }`}
                      >
                        <h3
                          className="text-[22px] desktop:text-[32px] font-semibold mb-2 inline-block bg-clip-text text-transparent"
                          style={{
                            backgroundImage:
                              "linear-gradient(to right,#306DA7 0%,#003350 100%)",
                            backgroundSize: "100% 100%",
                            WebkitBackgroundClip: "text",
                            backgroundClip: "text",
                          }}
                        >
                          {item.title}
                        </h3>

                        <div
                          className="text-[#003350] text-[16px] desktop:text-base"
                          dangerouslySetInnerHTML={{
                            __html: item?.field_content.value,
                          }}
                        />
                      </div>

                      <div className="flex flex-col items-center">
                        <div className="w-7 h-7 rounded-full bg-[#306DA7] shadow-lg z-10 relative flex-shrink-0" />
                        {index < opportunityCards.length - 1 && (
                          <div className="desktop:hidden flex-1 w-1 h-12 bg-[#306DA7] mt-2" />
                        )}
                      </div>

                      <div className="flex-1 flex items-center justify-center">
                        <Image
                          src={`${config.apiBase}${item?.field_image_icon?.uri?.url}`}
                          alt={item.title || item.field_title || "Card icon"}
                          width={288}
                          height={256}
                          className="mx-auto block mobileMax:w-[120px] betweenMobileTab:w-[180px] mobileMax:object-contain"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Energy efficiency life */}
        <div
          id="energy-efficiency-life"
          className="!overflow-hidden relative bg-[#003350] top-gray-ellipsed-img mobileMax:pt-0 betweenMobileTab:pt-12 py-[120px]"
        >
          {/* <img
            src="/static/images/gray-curve.png"
            alt="curve"
            className="absolute z-[3] w-full top-[-14px] h-[18px]"
          /> */}
          <div className="mini-container z-[2] relative">
            <div className="flex items-center mobileToDesk:flex-col">
              <div className="flex flex-col w-[50%] mobileToDesk:w-full mobileToDesk:mb-10">
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0,
                  }}
                  className="remove-animation-fluctuation mr-10 mobileToDesk:w-full mobileToDesk:mr-0 mobileToDesk:mt-6 mobileToDesk:order-2"
                >
                  <h3 className="remove-animation-fluctuation font-semibold desktop:text-[32px] mb-[30px] leading-normal text-transparent bg-[linear-gradient(to_right,#48DBB2_0%,#4fc0ff_100%)] font-semibold text-clip bg-clip-text text-[48px] mobileMax:text-[28px] mobileMax:px-0 mobileMax:mb-5">
                    {field_energy_efficient_title}
                  </h3>
                  <div
                    className="mobileMax:mb-5 text-left text-white text-[16px] leading-normal --font-poppins mobileMax:text-small"
                    dangerouslySetInnerHTML={{
                      __html: field_energy_efficient_content.value,
                    }}
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0,
                  }}
                  className="remove-animation-fluctuation mr-10 mobileToDesk:w-full mobileToDesk:mr-0 mobileToDesk:mt-6 mobileToDesk:order-2"
                >
                  <h3 className="remove-animation-fluctuation  desktop:text-[32px] mb-[30px] leading-normal text-transparent bg-[linear-gradient(to_right,#48DBB2_0%,#4fc0ff_100%)] font-semibold text-clip bg-clip-text text-[48px] mobileMax:text-[28px] mobileMax:px-0 mobileMax:mb-5">
                    {field_enery_objective_title}
                  </h3>
                  <div
                    className="mobileMax:mb-5 text-left text-white text-[16px] leading-normal --font-poppins mobileMax:text-small"
                    dangerouslySetInnerHTML={{
                      __html: field_energy_objective_content.value,
                    }}
                  />
                </motion.div>
              </div>
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0,
                }}
                className="remove-animation-fluctuation w-[50%] overflow-hidden h-full rounded-[50px] mobileToDesk:rounded-[30px] mobileToDesk:w-full mobileToDesk:order-1"
              >
                <Image
                  src={`${config.apiBase}${
                    field_energy_efficient_image?.uri?.url || ""
                  }`}
                  alt="grid-img"
                  height={350}
                  unoptimized={true}
                  width={350}
                  objectFit="cover"
                  className="w-full h-full max-w-full mobileToDesk:max-h-[500px] mobileMax:max-h-[300px] card-shadow object-cover transform transition-transform duration-500 hover:scale-105"
                />
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0,
              }}
              className="mt-12 mobileMax:mt-8 remove-animation-fluctuation "
            >
              <h5 className="text-transparent bg-[linear-gradient(to_right,#48DBB2_0%,#4fc0ff_100%)] font-semibold text-clip bg-clip-text tracking-tight text-[35px] leading-normal mb-8 text-center text-numans mobileMax:text-[28px] mobileMax:mb-5 mt-20 mobileMax:mt-10">
                {field_energy_activity_title}
              </h5>
              {/* activity cards */}

              <motion.div
                className={`remove-animation-fluctuation flex items-start justify-center gap-[70px] mobileToDesk:gap-[20px] flex-wrap mt-[60px]`}
                initial={isMobile ? { opacity: 0, y: 40 } : "hidden"}
                whileInView={isMobile ? { opacity: 1, y: 0 } : "visible"}
                viewport={{ once: true, amount: 0.2 }}
                variants={{
                  visible: {
                    transition: {
                      staggerChildren: 0.8,
                      delayChildren: 0.5,
                    },
                  },
                }}
              >
                {field_energy_activity_cards?.map(
                  (feature: DrupalNode, idx: number) => (
                    <motion.div
                      key={idx}
                      className="flex flex-col items-center text-center w-[25%] mobileToDesk:w-[48%] mb-3 mobileMax:!w-full mobileMax:px-3"
                      variants={{
                        hidden: { opacity: 0, y: idx % 2 === 0 ? -40 : 40 },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            type: "spring",
                            stiffness: 50,
                            damping: 15,
                            mass: 0.7,
                          },
                        },
                      }}
                    >
                      {idx % 2 === 0 ? (
                        <>
                          <div className="flex items-center flex-col mobileMax:w-full">
                            <Image
                              src={`${config.apiBase}${feature?.field_image_icon?.uri?.url}`}
                              alt="grid-img"
                              height={140}
                              width={168}
                              className="h-full w-[140px] block mx-auto"
                            />
                          </div>

                          <button
                            className={`mt-[20px] w-[278.76px] font-poppins bg-[linear-gradient(to_left,#4fc0ff,#5de29b)] flex items-center justify-center cursor-default text-[#003350] text-[20px] font-bold py-[8px] rounded-[123px] !no-underline mobileMax:text-xmedium px-2`}
                          >
                            {feature?.title}
                          </button>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: feature?.field_content?.processed,
                            }}
                            className={`mt-[32px] text-center text-[18px] text-[#fff] font-poppins font-semibold mobileMax:text-xsmall leading-normal mobileMax:order-3`}
                          />
                        </>
                      ) : (
                        <>
                          <button
                            className={`w-[278.76px] font-poppins bg-[linear-gradient(to_left,#4fc0ff,#5de29b)] flex items-center justify-center cursor-default text-[#003350] text-[20px] font-bold py-[8px]
                    rounded-[123px] !no-underline mobileMax:text-xmedium mobileMax:order-2`}
                          >
                            {feature?.title}
                          </button>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: feature?.field_content?.processed,
                            }}
                            className={`mt-[32px] text-center text-[18px] text-[#fff] font-poppins font-semibold mobileMax:text-xsmall leading-normal mobileMax:order-3`}
                          />
                          <div className="flex items-center flex-col mobileMax:!rotate-180 mobileMax:w-full mobileMax:order-1 mobileMax:mb-[17px] mt-[15px]">
                            <Image
                              src={`${config.apiBase}${feature?.field_image_icon?.uri?.url}`}
                              alt="grid-img"
                              height={140}
                              width={168}
                              className="h-full w-[140px] block mx-auto"
                            />
                          </div>
                        </>
                      )}
                    </motion.div>
                  ),
                )}
              </motion.div>
            </motion.div>
          </div>

          {/* ready to joining cards */}
        </div>

        <div className="py-[80px] mobileToDesk:py-[60px] mini-container">
          <div className="flex flex-wrap justify-start box-border mt-12 mb-6 mobileMax:mt-0">
            {/* ready cards */}
            <div className="flex items-center justify-between mb-14 mobileMax:mb-8 mobileToDesk:flex-col">
              <div className="w-[50%] mobileToDesk:w-full mobileToDesk:mb-10">
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0,
                  }}
                  id="joining-card"
                  className="remove-animation-fluctuation mb-8 mobileMax:mb-5 font-semibold text-cardText leading-normal --font-poppins text-[32px] mobileMax:text-small bg-[linear-gradient(8.97deg,_#11375C_-26.31%,_#4992D3_154.54%)] text-transparent bg-clip-text text-clip"
                  dangerouslySetInnerHTML={{
                    __html: field_ready_to_join_content.value,
                  }}
                />
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0,
                  }}
                  className="remove-animation-fluctuation mobileToDesk:w-full mobileToDesk:order-2 mobileToDesk:mr-0 mobileToDesk:mt-6 mr-12 elevate-list-view text-[#003350] leading-normal --font-poppins mobileMax:text-small"
                  dangerouslySetInnerHTML={{
                    __html: field_ready_to_move_section.value,
                  }}
                />
              </div>
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0,
                }}
                className="remove-animation-fluctuation w-[50%] mobileToDesk:w-full mobileToDesk:order-1"
              >
                <div className="h-full w-full rounded-[40px] mobileToDesk:rounded-[30px] mobileToDesk:order-1 overflow-hidden">
                  <Image
                    src={`${config.apiBase}${
                      field_ready_to_move_main_image?.uri?.url || ""
                    }`}
                    alt="grid-img"
                    height={350}
                    width={350}
                    unoptimized={true}
                    objectFit="cover"
                    className="w-full h-full max-w-full max-h-[600px] mobileToDesk:max-h-full card-shadow rounded-[12px] object-cover transform transition-transform duration-500 hover:scale-105"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* joining banner */}
        {field_campaign_life_banner?.map(
          (bannerCard: DrupalNode, index: number) => {
            return (
              <>
                <div
                  key={index}
                  className="py-14 elevate-joining-banner relative"
                  id="join-energy-campaign"
                  style={{
                    backgroundImage: `url(${
                      config.apiBase + bannerCard?.field_image_icon?.uri?.url
                    })`,
                  }}
                >
                  <div className="absolute top-0 left-0 bg-blackHighOpacity h-full w-full" />
                  <div className="mini-container relative z-[2]">
                    <motion.h3
                      initial={{ opacity: 0, y: 40 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0,
                      }}
                      className="remove-animation-fluctuation font-bold desktop:text-[55px] text-numans tab:min-h-[300px] mobileMax:min-h-[200px] desktop:leading-[85px] text-white text-[48px] leading-normal mobileMax:text-[32px] mobileMax:mb-10 text-center"
                    >
                      {bannerCard?.title}
                    </motion.h3>
                  </div>
                </div>
              </>
            );
          },
        )}

        <div className="relative bg-mapGray overflow-hidden">
          <div className="mini-container relative z-[2]">
            {/* Campaign material */}
            <div className="my-16 mobileMax:my-8">
              <motion.h3
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0,
                }}
                className="remove-animation-fluctuation text-[48px] text-numans mb-8 mobileMax:mb-5 leading-normal text-center font-semibold bg-[linear-gradient(to_top,#8EBCE5,#11375C)] text-clip mobileMax:text-[28px]"
              >
                {field_campaign_materials_title}
              </motion.h3>
              <div className="flex flex-wrap justify-start box-border mt-8 mobileMax:mt-6">
                {field_elevate_campaign_materials.map(
                  (materialCard: DrupalNode, index: number) => {
                    return (
                      <motion.div
                        key={index}
                        className="remove-animation-fluctuation px-[15px] mb-[25px] w-[33%] mobileMax:w-full mt-2 mobileMax:px-0 lieTablets:w-[50%] mobileMax:mb-5 mobileMax:mt-0"
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{
                          duration: 0,
                        }}
                      >
                        <div className="border-2 border-transparent hover:border-[#8EBCE5] transition rounded-xl bg-white p-5 h-full flex items-center flex-col box-border w-full card-shadow">
                          <div className="h-[80%]">
                            <div className="mb-5 mx-auto h-[50px] w-[50px] rounded-[12px] overflow-hidden">
                              <Image
                                src={`${config.apiBase}/sites/default/files/2023-12/suff-icon.png`}
                                alt="category img"
                                height={50}
                                width={50}
                                className="h-full w-full"
                              />
                            </div>
                            <h4 className="text-numans text-center text-[22px] mb-4 text-transparent bg-clip-text text-clip bg-[linear-gradient(to_top,#8EBCE5,#11375C)] leading-7 mobileMax:text-medium mobileMax:leading-7 font-medium">
                              {materialCard?.title}
                            </h4>
                          </div>
                          {materialCard?.field_link ? (
                            <Link
                              href={materialCard?.field_link || "" || "#"}
                              target="_blank"
                              className="h-[20%] mobileMax:w-full flex items-center mt-5 rounded-[23px] justify-center text-small text-white bg-[linear-gradient(to_right,#316FA8,#003350)] get-involve-btn font-mediums --font-poppins !px-8 min-h-[35px]"
                            >
                              {materialCard?.field_button_text}
                            </Link>
                          ) : (
                            <div className="h-[20%] mobileMax:w-full flex items-center mt-5 rounded-[23px] justify-center text-small text-white bg-[linear-gradient(to_right,#316FA8,#003350)] cursor-not-allowed get-involve-btn font-mediums --font-poppins !px-8 min-h-[35px]">
                              {materialCard?.field_button_text}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    );
                  },
                )}
              </div>
            </div>
            {/* campaign resources */}
            <div className="mb-16 mobileMax:mb-0">
              <motion.h3
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0,
                }}
                className="remove-animation-fluctuation text-[48px] text-numans mb-10 mobileMax:mb-5 leading-normal text-center font-semibold bg-[linear-gradient(to_top,#8EBCE5,#11375C)] text-clip mobileMax:text-[28px]"
              >
                {field_campaign_resources_title}
              </motion.h3>
              <div className="flex items-start flex-wrap mt-8 lieExactTab:mt-10 lieExactTab:flex-col">
                {field_elevate_campaign_resources
                  ?.slice(0, 1)
                  .map((resourceCard: DrupalNode, index: number) => {
                    const isEven = index % 2 !== 0;

                    return (
                      <div
                        key={index}
                        className="flex items-start justify-between mb-24 last:mb-0 lieExactTab:mb-6 lieExactTab:flex-col"
                      >
                        <motion.div
                          initial={{ opacity: 0, y: 40 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{
                            duration: 0,
                          }}
                          className={`remove-animation-fluctuation ${
                            isEven && "order-2"
                          } w-full laptop:flex-1 h-full lieExactTab:order-1 rounded-[12px] overflow-hidden laptop:max-h-[700px] max-h-[900px]`}
                        >
                          <Image
                            src={`${config.apiBase}${resourceCard?.field_image_icon?.uri?.url}`}
                            alt="campaign resources"
                            height={400}
                            width={400}
                            objectFit="cover"
                            className=" h-full w-full card-shadow transform transition-transform duration-500 hover:scale-105"
                          />
                        </motion.div>
                        <motion.div
                          initial={{ opacity: 0, y: 45 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{
                            duration: 0,
                          }}
                          className={`remove-animation-fluctuation ${
                            isEven
                              ? "order-1 mr-16 lieExactTab:mr-0 lieExactTab:mt-8"
                              : "ml-16 lieExactTab:ml-0 lieExactTab:mt-8 "
                          } lieExactTab:flex lieExactTab:flex-col lieExactTab:items-center lieExactTab:justify-center laptop:flex-1 h-full lieExactTab:order-1 lieExactTab:pb-12`}
                        >
                          <h5 className="text-[#003350] lieExactTab:text-center text-[35px] mb-[30px] lieTablets:mb-5 lieTablets:text-[25px] leading-normal mobileMax:text-medium mobileMax:mb-3 uppercase">
                            {resourceCard?.title}
                          </h5>
                          <div
                            className="--font-poppins lieExactTab:text-center text-left text-[#003350] text-medium leading-normal --font-poppins mobileMax:text-small mb-8 mobileMax:mb-5 elevate-link"
                            dangerouslySetInnerHTML={{
                              __html: resourceCard.field_content.value,
                            }}
                          />
                          <Link
                            href={resourceCard?.field_link || "#"}
                            target={`_blank`}
                            className="h-[46px] rounded-md bg-[linear-gradient(to_right,#316FA8,#003350)] px-[30px] flex max-w-[160px] items-center justify-center mt-10 text-[#fff] font-mediums --font-poppins text-xsmall"
                          >
                            {resourceCard?.field_button_text}
                          </Link>
                        </motion.div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
        {/* The power of energy efficiency */}
        <div
          id="power-energy-efficiency"
          className="pb-14 relative bg-white top-gray-ellipsed-img !overflow-hidden"
        >
          {/* <img
            src="/static/images/gray-curve.png"
            alt="curve"
            className="absolute z-[3] w-full top-[-14px] h-[18px]"
          /> */}
          <div className="mini-container relative z-[2]">
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0,
              }}
              className="remove-animation-fluctuation mt-[90px] desktop:text-[48px] font-semibold mb-[30px] leading-normal text-center bg-[linear-gradient(to_top,#8EBCE5,#11375C)] text-clip text-[48px] mobileMax:text-[28px] mobileMax:px-0 mobileMax:mb-5"
            >
              {field_power_energy_title}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0,
              }}
              className="remove-animation-fluctuation  --font-poppins text-center text-cardText text-medium leading-6 mobileMax:text-small mobileMax:leading-normal mb-12 mobileMax:mb-8"
            >
              {field_power_energy_subtitle}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0,
              }}
              className="remove-animation-fluctuation flex items-center justify-between overflow-hidden mobileToDesk:flex-col"
            >
              <div className="w-[50%] mobileToDesk:w-full mobileToDesk:mb-6">
                <Image
                  src={`${config.apiBase}${field_power_energy_image?.uri?.url || ""}`}
                  alt="grid-img"
                  height={350}
                  width={350}
                  objectFit="cover"
                  className="w-full h-full max-w-full object-cover max-h-[410px] rounded-[40px] overflow-hidden"
                />
              </div>
              <motion.div
                className="w-[50%] mobileToDesk:w-full mobileToDesk:ml-0 ml-12 mission-Pledge-Card elevate-power-list text-cardText text-medium leading-normal --font-poppins mobileMax:text-small"
                dangerouslySetInnerHTML={{
                  __html: field_power_energy_content.value,
                }}
              />
            </motion.div>
            <div className="mt-12 mobileMax:mt-8">
              <div className="flex-col desktop:flex-row desktop:flex items-start w-full">
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0,
                  }}
                  className="remove-animation-fluctuation desktop:w-[49%] desktop:min-h-[270px] w-full desktop:mr-5 mb-5 desktop:mb-0 flex items-center justify-between rounded-[30px] gradient-border-bg overflow-hidden"
                >
                  <motion.div className="w-full py-3 px-5 flex flex-col justify-start">
                    <motion.h5 className="h-full mb-5 text-clip bg-[linear-gradient(to_top,#8EBCE5,#11375C)] tracking-tight text-[35px] leading-normal text-center mobileMax:text-[28px]">
                      {field_enery_objective_title}
                    </motion.h5>
                    <motion.div
                      className="elevate-list-view text-cardText text-medium leading-normal --font-poppins mobileMax:text-small"
                      dangerouslySetInnerHTML={{
                        __html: field_power_objective_content.value,
                      }}
                    />
                  </motion.div>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 60 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0,
                  }}
                  className="remove-animation-fluctuation desktop:w-[49%] desktop:min-h-[270px] w-full flex items-center justify-between rounded-[30px] gradient-border-bg overflow-hidden"
                >
                  <motion.div className="w-full py-3 px-5 flex flex-col justify-start">
                    <motion.h5 className="h-full mb-5 text-clip bg-[linear-gradient(to_top,#8EBCE5,#11375C)] tracking-tight text-[35px] leading-normal text-center mobileMax:text-[28px]">
                      {field_energy_activity_title}
                    </motion.h5>
                    <motion.div
                      className="elevate-list-view text-cardText text-medium leading-normal --font-poppins mobileMax:text-small"
                      dangerouslySetInnerHTML={{
                        __html: field_power_activity_content.value,
                      }}
                    />
                  </motion.div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative overflow-hidden bg-white">
          <div className="mini-container relative z-[2]">
            {/* campaign resources */}
            <div className="mb-16 mobileMax:mb-0">
              <div className="flex  items-start flex-wrap mt-8 lieExactTab:mt-10 lieExactTab:flex-col">
                {field_elevate_campaign_resources
                  ?.slice(1, 2)
                  .map((resourceCard: DrupalNode, index: number) => {
                    const isEven = index % 2 !== 0;

                    return (
                      <div
                        key={index}
                        className="flex flex-row-reverse items-start justify-between mb-24 last:mb-0 lieExactTab:mb-6 lieExactTab:flex-col"
                      >
                        <motion.div
                          initial={{ opacity: 0, y: 40 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{
                            duration: 0,
                          }}
                          className={`remove-animation-fluctuation ${
                            isEven && "order-2"
                          } w-full laptop:flex-1 h-full lieExactTab:order-1 rounded-[12px] overflow-hidden laptop:max-h-[700px] max-h-[900px]`}
                        >
                          <Image
                            src={`${config.apiBase}${resourceCard?.field_image_icon?.uri?.url}`}
                            alt="campaign resources"
                            height={400}
                            width={400}
                            objectFit="cover"
                            className=" h-full w-full card-shadow transform transition-transform duration-500 hover:scale-105"
                          />
                        </motion.div>
                        <motion.div
                          initial={{ opacity: 0, y: 45 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{
                            duration: 0,
                          }}
                          className={`remove-animation-fluctuation ${
                            isEven
                              ? "order-1 mr-16 lieExactTab:mr-0 lieExactTab:mt-8"
                              : "ml-16 lieExactTab:ml-0 lieExactTab:mt-8 "
                          } lieExactTab:flex lieExactTab:flex-col lieExactTab:items-center lieExactTab:justify-center laptop:flex-1 h-full lieExactTab:order-1 lieExactTab:pb-12`}
                        >
                          <h5 className="text-[#003350] lieExactTab:text-center text-[35px] mb-[30px] lieTablets:mb-5 lieTablets:text-[25px] leading-normal mobileMax:text-medium mobileMax:mb-3 uppercase">
                            {resourceCard?.title}
                          </h5>
                          <div
                            className="--font-poppins lieExactTab:text-center text-left text-cardText text-medium
										                         leading-normal --font-poppins mobileMax:text-small mb-8 mobileMax:mb-5 elevate-link"
                            dangerouslySetInnerHTML={{
                              __html: resourceCard.field_content.value,
                            }}
                          />
                          <Link
                            href="/resources/missionefficiency-actionsplaybook.pdf"
                            target={`_blank`}
                            download="missionefficiency-actionsplaybook.pdf"
                            className="h-[46px] rounded-md bg-[linear-gradient(to_right,#316FA8,#003350)] px-[30px] flex max-w-[160px] items-center justify-center mt-10 text-[#FFF] font-mediums --font-poppins text-xsmall"
                          >
                            {resourceCard?.field_button_text}
                          </Link>
                        </motion.div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
      </div>
      <FindOutSection data={field_elevate_bottom_footer} />
      {/* <Toaster /> */}

      <PledgeSuccessModal
        open={openSuccessModal}
        elevatePage={true}
        setOpen={(value) => setOpenSuccessModal(value)}
      />
    </CaptchaProvider>
  );
};

export default ElevateScreen;
